import { call, put, takeLatest } from 'redux-saga/effects';
import type { PayloadAction } from '@reduxjs/toolkit';
import request from '@/utils/request';
import API from '@/config/api';
import type { Company, CompanyFilters, SavedFilter } from '@/types/company.types';
import {
  fetchCompaniesRequest, fetchCompaniesSuccess, fetchCompaniesFailure,
  fetchSavedFiltersRequest, fetchSavedFiltersSuccess, fetchSavedFiltersFailure,
  saveFilterRequest, saveFilterSuccess, saveFilterFailure,
  deleteFilterRequest, deleteFilterSuccess, deleteFilterFailure,
  fetchMarketingChannelsRequest, fetchMarketingChannelsSuccess, fetchMarketingChannelsFailure,
  deleteCompanyRequest, deleteCompanySuccess, deleteCompanyFailure,
  disableCompanyRequest, disableCompanySuccess, disableCompanyFailure,
  demoCompanyRequest, demoCompanySuccess, demoCompanyFailure,
  extendTrialRequest, extendTrialSuccess, extendTrialFailure,
  exportCsvRequest, exportCsvSuccess, exportCsvFailure,
} from './companiesSlice';

function* handleFetchCompanies(
  action: PayloadAction<{
    page: number; limit: number; filters: CompanyFilters;
    sortField?: string; sortOrder?: number; search?: string;
  }>,
) {
  try {
    const { page, limit, filters, sortField, sortOrder, search } = action.payload;
    const body: Record<string, unknown> = { page, limit, ...filters };
    if (sortField) { body.sortField = sortField; body.sortOrder = sortOrder ?? -1; }
    if (search) body.search = search;
    const res: { data: { data: { list: Company[]; totalRecords: number } } } = yield call(request.post, API.COMPANIES, body);
    yield put(fetchCompaniesSuccess({ list: res.data.data.list, totalCount: res.data.data.totalRecords }));
  } catch (e: unknown) {
    console.error('Failed to fetch companies:', e);
    yield put(fetchCompaniesFailure(e instanceof Error ? e.message : 'Failed to fetch companies'));
  }
}

function* handleFetchSavedFilters() {
  try {
    const res: { data: { result: SavedFilter[] } } = yield call(request.get, API.FILTER_LIST);
    yield put(fetchSavedFiltersSuccess(res.data.result));
  } catch (e: unknown) {
    console.error('Failed to fetch saved filters:', e);
    yield put(fetchSavedFiltersFailure(e instanceof Error ? e.message : 'Failed to fetch saved filters'));
  }
}

function* handleSaveFilter(action: PayloadAction<{ name: string; filters: CompanyFilters }>) {
  try {
    const res: { data: { result: SavedFilter } } = yield call(request.post, API.ADD_FILTER, action.payload);
    yield put(saveFilterSuccess(res.data.result));
  } catch (e: unknown) {
    console.error('Failed to save filter:', e);
    yield put(saveFilterFailure(e instanceof Error ? e.message : 'Failed to save filter'));
  }
}

function* handleDeleteFilter(action: PayloadAction<string>) {
  try {
    yield call(request.delete, `${API.DELETE_FILTER}/${action.payload}`);
    yield put(deleteFilterSuccess(action.payload));
  } catch (e: unknown) {
    console.error('Failed to delete filter:', e);
    yield put(deleteFilterFailure(e instanceof Error ? e.message : 'Failed to delete filter'));
  }
}

function* handleFetchMarketingChannels() {
  try {
    const res: { data: { result: string[] } } = yield call(request.get, API.MARKETING_CHANNELS);
    yield put(fetchMarketingChannelsSuccess(res.data.result));
  } catch (e: unknown) {
    console.error('Failed to fetch marketing channels:', e);
    yield put(fetchMarketingChannelsFailure(e instanceof Error ? e.message : 'Failed to fetch marketing channels'));
  }
}

function* handleDeleteCompany(action: PayloadAction<{ companyId: string }>) {
  try {
    yield call(request.post, API.DELETE_COMPANY, action.payload);
    yield put(deleteCompanySuccess(action.payload.companyId));
  } catch (e: unknown) {
    console.error('Failed to delete company:', e);
    yield put(deleteCompanyFailure(e instanceof Error ? e.message : 'Failed'));
  }
}

function* handleDisableCompany(action: PayloadAction<{ companyId: string; isDisabled: boolean }>) {
  try {
    yield call(request.post, API.DISABLE_COMPANY, action.payload);
    yield put(disableCompanySuccess());
  } catch (e: unknown) {
    console.error('Failed to disable company:', e);
    yield put(disableCompanyFailure(e instanceof Error ? e.message : 'Failed'));
  }
}

function* handleDemoCompany(action: PayloadAction<{ companyId: string; isDemo: boolean }>) {
  try {
    yield call(request.post, API.DEMO_COMPANY, action.payload);
    yield put(demoCompanySuccess());
  } catch (e: unknown) {
    console.error('Failed to update demo company:', e);
    yield put(demoCompanyFailure(e instanceof Error ? e.message : 'Failed'));
  }
}

function* handleExtendTrial(action: PayloadAction<{ companyId: string; days?: number }>) {
  try {
    yield call(request.post, API.EXTEND_FREE_TRIAL, action.payload);
    yield put(extendTrialSuccess());
  } catch (e: unknown) {
    console.error('Failed to extend trial:', e);
    yield put(extendTrialFailure(e instanceof Error ? e.message : 'Failed'));
  }
}

function* handleExportCsv(action: PayloadAction<CompanyFilters>) {
  try {
    const res: { data: Blob } = yield call(request.get, API.EXPORT_CSV, {
      params: action.payload,
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(res.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'companies-export.csv';
    link.click();
    window.URL.revokeObjectURL(url);
    yield put(exportCsvSuccess());
  } catch (e: unknown) {
    console.error('Failed to export CSV:', e);
    yield put(exportCsvFailure(e instanceof Error ? e.message : 'Failed to export CSV'));
  }
}

export default function* companiesSaga() {
  yield takeLatest(fetchCompaniesRequest.type, handleFetchCompanies);
  yield takeLatest(fetchSavedFiltersRequest.type, handleFetchSavedFilters);
  yield takeLatest(saveFilterRequest.type, handleSaveFilter);
  yield takeLatest(deleteFilterRequest.type, handleDeleteFilter);
  yield takeLatest(fetchMarketingChannelsRequest.type, handleFetchMarketingChannels);
  yield takeLatest(deleteCompanyRequest.type, handleDeleteCompany);
  yield takeLatest(disableCompanyRequest.type, handleDisableCompany);
  yield takeLatest(demoCompanyRequest.type, handleDemoCompany);
  yield takeLatest(extendTrialRequest.type, handleExtendTrial);
  yield takeLatest(exportCsvRequest.type, handleExportCsv);
}
